import { cloneMap, compareLocalizedValues, ensureMapEntry } from "../../utils";

/**
 * @param {IPermissionMatrix|undefined} permissionMatrix
 */
export function getPermissionMatrixObjects(permissionMatrix)
{
    if (!permissionMatrix) { return []; }
    const objects = Array.from(permissionMatrix.permissionObjects.values());
    objects.sort((a, b) => compareLocalizedValues(a.name, b.name));
    return objects;
}

/**
 * @param {{ roleId: number, objectId: number, permissions: { [key: number]: boolean } }[]} values
 * @returns {ValuesMap}
 */
export function permissionValuesToMap(values)
{
    const map = new Map();
    for (const { roleId, objectId, permissions } of values)
    {
        const roleMap = ensureMapEntry(map, roleId, () => new Map());
        const objectMap = ensureMapEntry(roleMap, objectId, () => new Map());
        for (const [permission, value] of Object.entries(permissions))
        {
            objectMap.set(Number(permission), value);
        }
    }
    return map;
}

// /**
//  * @param {Iterable<RoleRightEntity>} rolesRights
//  */
// export function rightsByRole(rolesRights)
// {
//     const map = new Map();
//     for (const right of rolesRights)
//     {
//         const { roleId, permissionObjectRightId } = right;
//         const roleMap = ensureMapEntry(map, roleId, () => new Map());
//         roleMap.set(permissionObjectRightId, right);
//     }
//     return map;
// }

/**
 * @param {PermissionEntity[]} permissions
 * @returns {Map<number, Map<number, PermissionEntity>>}
 */
export function permissionsByObjectByKind(permissions)
{
    const map = new Map();
    for (const permission of permissions)
    {
        const { permissionObjectId, kind } = permission;
        const objectPermissions = ensureMapEntry(map, permissionObjectId, () => new Map());
        if (objectPermissions.has(kind)) { throw new Error(`Duplicate permission ${permission.id} for object ${permissionObjectId} and kind ${kind}`); }
        objectPermissions.set(kind, permission);
    }
    return map;
}

/**
 * @param {RolePermissionEntity[]} rolesPermissions
 * @param {Map<number, PermissionEntity>} permissionMap
 * @returns {Map<number, Map<number, Map<number, RolePermissionEntity>>>}
 */
export function rolesPermissionsByRoleByObjectByKind(rolesPermissions, permissionMap)
{
    const map = new Map();
    for (const rolePermission of rolesPermissions)
    {
        const { roleId, permissionId } = rolePermission;
        const permission = permissionMap.get(permissionId);
        if (!permission) { throw new Error(`Permission with id ${permissionId} not found`); }
        const rolePermissions = ensureMapEntry(map, roleId, () => new Map());
        const objectPermissions = ensureMapEntry(rolePermissions, permission.permissionObjectId, () => new Map());
        if (objectPermissions.has(permission.kind))
            { throw new Error(`Duplicate permission kind ${permission.kind} for object ${permission.permissionObjectId} and role ${roleId}`); }
        objectPermissions.set(permission.kind, rolePermission);
    }
    return map;
}

/**
 * @param {OperationEntity[]} operations
 * @returns {Map<number, OperationEntity[]>}
 */
export function operationsByPermission(operations)
{
    const map = new Map();
    for (const operation of operations)
    {
        const { permissionId } = operation;
        const permissionOperations = ensureMapEntry(map, permissionId, () => []);
        permissionOperations.push(operation);
    }
    return map;
}

/**
 * @param {OperationEntity[]} operations
 * @param {Map<number, PermissionEntity>} permissionMap
 * @returns {Map<number, OperationEntity[]>}
 */
export function operationsByPermissionObject(operations, permissionMap)
{
    const map = new Map();
    for (const operation of operations)
    {
        const permission = permissionMap.get(operation.permissionId);
        if (!permission) { throw new Error(`Permission with id ${operation.permissionId} not found`); }
        ensureMapEntry(map, permission.permissionObjectId, () => []).push(operation);
    }
    return map;
}

/**
 * @param {ValuesMap} values
 * @returns {ValuesMap}
 */
export function cloneValuesMap(values)
{
    return cloneMap
    // eslint-disable-next-line no-unexpected-multiline
    (
        values, objects => cloneMap
        // eslint-disable-next-line no-unexpected-multiline
        (
            objects, permissions => cloneMap(permissions, value => value)
        )
    );
}

/**
 * @param {IPermissionMatrix} permissionMatrix
 * @param {import('@tanstack/react-table').Row<PermissionObjectEntity>} row
 * @param {string} _columnId
 * @param {string} value
 */
export function matchTextInPermissionObject(permissionMatrix, row, _columnId, value)
{
    const query = String(value || '').trim().toLowerCase();
    if (!query) { return true; }
    if (row.getIsGrouped()) { return true; }
    const permissionObject = row.original;
    const name = (permissionObject?.name ?? '').toString().toLowerCase();
    if (name.includes(query)) { return true; }
    const operations = permissionMatrix.getOperationsForPermissionObject(permissionObject.id);
    for (const operation of operations)
    {
        if (operation.name.toLowerCase().includes(query)) { return true; }
    }
    return operations.some(op => op?.name?.toLowerCase().includes(query));
}